import { AmbientGlow, Counter, GlassCard, ScrollReveal } from '@/components/design-system';
import type { ListingsResponse } from '@/lib/collections/listings';
import { computeHouseholdImpact } from '@/utils/impact-metrics';
import { Leaf, Recycle, Wallet } from 'lucide-react';

interface ImpactSummarySectionProps {
  completedListings: ListingsResponse[];
}

/**
 * Hero impact summary for the household wallet page: three animated count-up
 * stats computed live from the household's own completed listings. Nothing
 * here is hardcoded — every number derives from real data.
 */
export function ImpactSummarySection({ completedListings }: ImpactSummarySectionProps) {
  const { totalKg, totalUsdcEarned, itemsDiverted } = computeHouseholdImpact(completedListings);

  const stats = [
    {
      key: 'kg',
      icon: Recycle,
      label: 'Plastic recovered',
      value: totalKg,
      decimals: totalKg % 1 === 0 ? 0 : 1,
      suffix: ' kg',
      prefix: '',
      color: 'primary' as const,
    },
    {
      key: 'usdc',
      icon: Wallet,
      label: 'USDC earned',
      value: totalUsdcEarned,
      decimals: 2,
      suffix: '',
      prefix: '$',
      color: 'accent' as const,
    },
    {
      key: 'items',
      icon: Leaf,
      label: 'Items diverted from landfill',
      value: itemsDiverted,
      decimals: 0,
      suffix: '',
      prefix: '',
      color: 'amber' as const,
    },
  ];

  return (
    <div className="relative">
      <AmbientGlow color="primary" size={360} position="top-0 left-0 -translate-x-1/3 -translate-y-1/3" />
      <AmbientGlow color="accent" size={320} position="bottom-0 right-0 translate-x-1/4 translate-y-1/4" />
      <div className="relative grid gap-4 sm:grid-cols-3">
        {stats.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <ScrollReveal key={stat.key} delay={i * 0.08}>
              <GlassCard padding="lg" className="h-full text-center sm:text-left">
                <div
                  className={
                    stat.color === 'primary'
                      ? 'mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/15 ring-1 ring-primary/30 sm:mx-0'
                      : stat.color === 'accent'
                        ? 'mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-accent/15 ring-1 ring-accent/30 sm:mx-0'
                        : 'mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-[hsl(var(--amber))]/15 ring-1 ring-[hsl(var(--amber))]/30 sm:mx-0'
                  }
                >
                  <Icon
                    className={
                      stat.color === 'primary'
                        ? 'h-5 w-5 text-primary'
                        : stat.color === 'accent'
                          ? 'h-5 w-5 text-accent'
                          : 'h-5 w-5 text-[hsl(var(--amber))]'
                    }
                    strokeWidth={2}
                  />
                </div>
                <p className="mt-4 text-3xl font-bold text-foreground sm:text-4xl">
                  <Counter value={stat.value} decimals={stat.decimals} prefix={stat.prefix} suffix={stat.suffix} />
                </p>
                <p className="mt-1 text-sm text-muted-foreground">{stat.label}</p>
              </GlassCard>
            </ScrollReveal>
          );
        })}
      </div>
    </div>
  );
}

export default ImpactSummarySection;
